import { css, StyleSheet } from 'aphrodite';
import { FC } from 'react';
import { brandPrimary, charmGray, shuffleGray, white } from '../../../../web/styles/colors';
import { bs } from '../../../../web/styles/styles';

export type PreviewContext = 'blog' | 'survey' | 'meeting';

interface PreviewContextTabsProps {
	selectedContext: PreviewContext;
	onContextChange: (context: PreviewContext) => void;
	/** Whether the blog feature is enabled (show tab) */
	blogFeatureEnabled?: boolean;
	/** Whether blog content exists (enable tab) */
	blogAvailable?: boolean;
	/** Whether the survey feature is enabled (show tab) */
	surveyFeatureEnabled?: boolean;
	/** Whether survey content exists (enable tab) */
	surveyAvailable: boolean;
	/** Whether the meeting feature is enabled (show tab) */
	meetingFeatureEnabled?: boolean;
	/** Whether meeting content exists (enable tab) */
	meetingAvailable: boolean;
}

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		gap: 8,
		marginBottom: 16,
	},
	tab: {
		padding: '8px 16px',
		border: `1px solid ${shuffleGray}`,
		borderRadius: 6,
		backgroundColor: white,
		color: charmGray,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: 500,
		transition: 'all 0.15s ease',
		':hover:not(:disabled)': {
			borderColor: brandPrimary,
			color: brandPrimary,
		},
	},
	selectedTab: {
		backgroundColor: brandPrimary,
		borderColor: brandPrimary,
		color: white,
		':hover:not(:disabled)': {
			backgroundColor: brandPrimary,
			borderColor: brandPrimary,
			color: white,
		},
	},
	disabledTab: {
		opacity: 0.5,
		cursor: 'not-allowed',
	},
});

/**
 * Tab bar for selecting the preview context (Blog, Survey, or Meeting).
 * Allows admins to preview how their header/footer customizations will
 * appear in different contexts.
 *
 * Tabs are shown based on whether the feature is enabled.
 * Tabs are enabled/disabled based on whether content exists to preview.
 */
export const PreviewContextTabs: FC<PreviewContextTabsProps> = ({
	selectedContext,
	onContextChange,
	blogFeatureEnabled = true,
	blogAvailable = true,
	surveyFeatureEnabled = false,
	surveyAvailable,
	meetingFeatureEnabled = false,
	meetingAvailable,
}) => {
	const tabs: { id: PreviewContext; label: string; visible: boolean; available: boolean; tooltip?: string }[] = [
		{
			id: 'blog',
			label: 'Blog',
			visible: blogFeatureEnabled,
			available: blogAvailable,
			tooltip: !blogAvailable ? 'Create a blog post to preview' : undefined,
		},
		{
			id: 'survey',
			label: 'Survey',
			visible: surveyFeatureEnabled,
			available: surveyAvailable,
			tooltip: !surveyAvailable ? 'Create a satisfaction survey to preview' : undefined,
		},
		{
			id: 'meeting',
			label: 'Meeting',
			visible: meetingFeatureEnabled,
			available: meetingAvailable,
			tooltip: !meetingAvailable ? 'Set up meeting scheduler to preview' : undefined,
		},
	];

	const visibleTabs = tabs.filter(tab => tab.visible);

	return (
		<div className={css(styles.container)}>
			<span className={css(bs.textSm, bs.textGray600, bs.mr2, bs.itemsCenter)}>Preview:</span>
			{visibleTabs.map(tab => (
				<button
					key={tab.id}
					type='button'
					className={css(
						styles.tab,
						selectedContext === tab.id && styles.selectedTab,
						!tab.available && styles.disabledTab
					)}
					onClick={() => tab.available && onContextChange(tab.id)}
					disabled={!tab.available}
					title={tab.tooltip}
				>
					{tab.label}
				</button>
			))}
		</div>
	);
};
